import { Product } from '@df/catalog/product/product';
import { HtmlTagOptions, TextStyleOptions, UndefinedOption } from '@df/ui/atomic/atomic-options';
import { ucfirst } from '@df/util/fn/ucfirst.fn';
import { NICmsContentField } from '@icms/core/content';
import findIndex from 'lodash-es/findIndex';
import { CMS_COMPONENT_CWV_CONFIG } from '../../../../config/component.config';
import { getFields as getCoreFields } from '../model-carousel/model-carousel.config';
import { EModelCarouselVariant } from '../model-carousel/model-carousel.interface';
import { EModelProductLabel, EModelProductView, ILocalModelCarouselData } from './local-model-carousel.interface';

export function getFields(
    type: Product['type'] | string,
    pageType?: string,
    enableCustomFields?: boolean,
    enableCarouselFields?: boolean
): NICmsContentField.IField[] {
    const fields = getCoreFields(type, 36, pageType);

    fields.splice(2, 0, <NICmsContentField.IChoiceField>{
        key: 'title_tag',
        type: NICmsContentField.EFieldType.choice,
        title: 'Force Title Tag',
        description: 'For SEO reasons you may want to force particular HTML tag',
        options: HtmlTagOptions,
        default: 'h2'
    });

    fields.splice(3, 0, <NICmsContentField.IField>{
        key: 'title_color',
        type: NICmsContentField.EFieldType.colorPicker,
        title: 'Title Color'
    });

    fields.splice(4, 0, {
        key: 'cta_text',
        type: NICmsContentField.EFieldType.text,
        title: 'CTA Text'
    });

    fields.splice(5, 0, {
        key: 'cta_link',
        type: NICmsContentField.EFieldType.text,
        title: 'CTA Link',
        description: 'If not set then it will be a collection link based on the selected category'
    });

    fields.splice(6, 0, <NICmsContentField.IField>{
        key: 'cta_color',
        type: NICmsContentField.EFieldType.colorPicker,
        title: 'CTA Color'
    });

    if (enableCustomFields) {
        fields.splice(8, 1, <NICmsContentField.IChoiceField>{
            key: 'variant',
            required: true,
            type: NICmsContentField.EFieldType.choice,
            title: 'Source type',
            options: [
                { key: 'custom', title: 'Custom elements' },
                {
                    key: EModelCarouselVariant.ModelsIds,
                    title: ucfirst(type.split('-').join(' ')) + ' selection'
                },
                type === 'product'
                    ? {
                          key: EModelCarouselVariant.CategoryProducts,
                          title: 'Category Selection'
                      }
                    : undefined
            ].filter(i => !!i),
            default: type === 'product' ? EModelCarouselVariant.CategoryProducts : EModelCarouselVariant.ModelsIds
        });
    }

    if (type === Product.type) {
        // hide the `ids` field
        const idsIndex = findIndex(fields, i => i.key === 'ids');
        const ids = <NICmsContentField.IRelationField>fields[idsIndex];

        ids.canShow = () => false;

        fields.push(<NICmsContentField.IRepeaterField>{
            key: 'items',
            type: NICmsContentField.EFieldType.repeater,
            title: 'Products',
            itemName: 'Product',
            itemNameField: 'id',
            fields: [
                <NICmsContentField.IRelationField>{
                    key: 'id',
                    type: NICmsContentField.EFieldType.relation,
                    title: 'Product ID',
                    options: {
                        title: 'Product',
                        type: 'product',
                        limit: 1,
                        multiple: false
                    }
                },
                <NICmsContentField.IChoiceField>{
                    key: 'product_view',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'View',
                    description: 'It has priority over Display Options',
                    options: [
                        ...UndefinedOption,
                        { key: EModelProductView.Product, title: EModelProductView.Product },
                        { key: EModelProductView.Model, title: EModelProductView.Model }
                    ]
                },
                <NICmsContentField.IChoiceField>{
                    key: 'product_label',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Label',
                    description: 'It has priority over Display Options',
                    options: [
                        ...UndefinedOption,
                        { key: EModelProductLabel.Base, title: EModelProductLabel.Base },
                        { key: EModelProductLabel.Alternative, title: EModelProductLabel.Alternative }
                    ]
                }
            ],
            canShow: (data: ILocalModelCarouselData) => data?.variant === EModelCarouselVariant.ModelsIds
        });

        fields.push(
            ...[
                // product
                <NICmsContentField.IField>{
                    type: NICmsContentField.EFieldType.label,
                    key: 'section-product',
                    title: 'Display Options'
                },
                <NICmsContentField.IChoiceField>{
                    key: 'product_view',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'View',
                    options: [
                        ...UndefinedOption,
                        { key: EModelProductView.Product, title: EModelProductView.Product },
                        { key: EModelProductView.Model, title: EModelProductView.Model }
                    ],
                    default: EModelProductView.Product
                },
                <NICmsContentField.IChoiceField>{
                    key: 'product_label',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Label',
                    description: 'Display image label',
                    options: [
                        ...UndefinedOption,
                        { key: EModelProductLabel.Base, title: EModelProductLabel.Base },
                        { key: EModelProductLabel.Alternative, title: EModelProductLabel.Alternative }
                    ],
                    default: EModelProductLabel.Alternative
                },
                <NICmsContentField.IField>{
                    key: 'product_hide_price',
                    type: NICmsContentField.EFieldType.toggle,
                    title: 'Hide Price',
                    description: 'Hide product price',
                    default: false
                },
                <NICmsContentField.IField>{
                    key: 'product_hide_name',
                    type: NICmsContentField.EFieldType.toggle,
                    title: 'Hide Name',
                    description: 'Hide product name',
                    default: false
                },
                <NICmsContentField.IField>{
                    key: 'product_hide_label',
                    type: NICmsContentField.EFieldType.toggle,
                    title: 'Hide Label',
                    description: 'Hide product label',
                    default: false
                },
                <NICmsContentField.IField>{
                    key: 'product_hide_video',
                    type: NICmsContentField.EFieldType.toggle,
                    title: 'Hide Video',
                    description: 'Hide product video',
                    default: false
                },
                <NICmsContentField.IField>{
                    key: 'product_list',
                    type: NICmsContentField.EFieldType.toggle,
                    title: 'Show List',
                    default: true
                }
            ]
        );
    }

    if (enableCustomFields) {
        const additionalCarouselFields = [
            <NICmsContentField.IChoiceField>{
                key: 'forceTitleTag',
                type: NICmsContentField.EFieldType.choice,
                title: 'Force Title Tag',
                description: 'For SEO reasons you may want to force particular HTML tag',
                options: HtmlTagOptions
            },
            <NICmsContentField.IChoiceField>{
                key: 'textStyle',
                type: NICmsContentField.EFieldType.choice,
                title: 'Text Style',
                options: TextStyleOptions,
                default: ''
            }
        ];

        const baseCarouselFields = [
            {
                type: NICmsContentField.EFieldType.label,
                key: 'section-custom-items',
                title: 'Custom Item'
            },
            <NICmsContentField.IField>{
                key: 'image',
                type: NICmsContentField.EFieldType.image,
                title: 'Image',
                required: true
            },
            <NICmsContentField.IField>{
                key: 'copy',
                type: NICmsContentField.EFieldType.text,
                title: 'Copy'
            },
            <NICmsContentField.IField>{
                key: 'url',
                type: NICmsContentField.EFieldType.text,
                title: 'Url'
            }
        ];
        if (enableCarouselFields) {
            baseCarouselFields.push(...additionalCarouselFields);
        }

        fields.push(
            ...[
                <NICmsContentField.IRepeaterField>{
                    type: NICmsContentField.EFieldType.repeater,
                    key: 'custom_items',
                    title: 'Custom elements',
                    itemName: 'custom element',
                    defaultSize: 1,
                    fields: [...baseCarouselFields],
                    canShow: (data: ILocalModelCarouselData) => data?.variant === EModelCarouselVariant.CustomElements
                }
            ]
        );
    }

    fields.push(
        ...[
            // carousel
            {
                type: NICmsContentField.EFieldType.label,
                key: 'carousel_section',
                title: 'Carousel',
                canShow: (data: ILocalModelCarouselData) => !data?.product_list
            },
            <NICmsContentField.IField>{
                key: 'carousel_loop',
                type: NICmsContentField.EFieldType.toggle,
                title: 'Carousel Loop',
                default: false,
                canShow: (data: ILocalModelCarouselData) => !data?.product_list
            },
            <NICmsContentField.IField>{
                key: 'carousel_marquee',
                type: NICmsContentField.EFieldType.toggle,
                title: 'Carousel Marquee',
                default: false,
                canShow: (data: ILocalModelCarouselData) => !data?.product_list
            },
            <NICmsContentField.INumberField>{
                key: 'carousel_marquee_speed',
                type: NICmsContentField.EFieldType.number,
                title: 'Carousel Marquee Speed',
                description: 'Step is 0.1',
                min: 0,
                max: 10,
                step: 0.1,
                default: 1,
                sanitize: (input: number) => {
                    const value = Math.max(0, Math.min(input, 10));

                    if (input < 0) {
                        return {
                            error: 'Carousel Marquee Speed must be greater than 0',
                            value
                        };
                    } else if (input > 10) {
                        return {
                            error: 'Carousel Marquee Speed must be less than 10',
                            value
                        };
                    }

                    return value;
                },
                canShow: (data: ILocalModelCarouselData) => !data?.product_list
            },
            <NICmsContentField.IField>{
                key: 'carousel_nested',
                type: NICmsContentField.EFieldType.checkbox,
                title: 'Carousel Nested',
                description: 'Use only on carousels that use same direction as the parent one',
                default: false,
                canShow: (data: ILocalModelCarouselData) => !data.product_list
            },
            <NICmsContentField.IField>{
                type: NICmsContentField.EFieldType.label,
                key: 'section-other',
                title: 'Other',
                canShow: (data: ILocalModelCarouselData) => !data?.product_list
            },
            <NICmsContentField.IField>{
                type: NICmsContentField.EFieldType.colorPicker,
                key: 'cursor_color',
                title: 'Drag Cursor Color',
                canShow: (data: ILocalModelCarouselData) => !data?.product_list
            }
        ],
        ...CMS_COMPONENT_CWV_CONFIG
    );

    return fields;
}
